import { useEffect, useState, type FormEvent } from "react";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, type AppMessage } from "@clevis/shared-types";
import { Button, Card, CardTitle, ConfirmButton, Input, Label, Select, cn } from "@clevis/shared-ui";
import { ListFooter, SearchBox, SortHeader, useListView } from "../../components/ListControls";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";

type Row = AppMessage & { id: string };
const SEVERITY_STYLE: Record<string, string> = {
  info: "bg-steel/15 text-ink",
  success: "bg-success/15 text-success",
  warning: "bg-brandorange/15 text-brandorange-dark",
  error: "bg-danger/15 text-danger",
};

export function AdminMessages() {
  const [messages, setMessages] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({
    title: "",
    body: "",
    severity: "info" as AppMessage["severity"],
    audience: "all" as AppMessage["audience"],
  });
  const [status, setStatus] = useState("all");
  const view = useListView(messages, {
    text: (m) => [m.title, m.body, m.severity, m.audience],
    sorts: {
      updated: (a, b) => b.updatedAt - a.updatedAt,
      title: (a, b) => a.title.localeCompare(b.title),
      severity: (a, b) => a.severity.localeCompare(b.severity),
    },
    defaultSort: "updated",
    filter: (m) => status === "all" || (status === "live" ? m.active : !m.active),
  });

  useEffect(() => {
    const q = query(collection(db, paths.appMessages()), orderBy("updatedAt", "desc"));
    return onSnapshot(q, (snap) => {
      setMessages(snap.docs.map((d) => ({ id: d.id, ...(d.data() as AppMessage) })));
    });
  }, []);

  async function create(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await call("upsertAppMessage", { id: null, ...form }, { mutation: false });
      setForm({ title: "", body: "", severity: "info", audience: "all" });
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(m: Row) {
    setError("");
    try {
      await call(
        "upsertAppMessage",
        {
          id: m.id,
          title: m.title,
          body: m.body,
          severity: m.severity,
          audience: m.audience,
          tenantIds: m.tenantIds,
          ctaLabel: m.ctaLabel,
          ctaUrl: m.ctaUrl,
          active: !m.active,
        },
        { mutation: false },
      );
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function remove(id: string) {
    setError("");
    try {
      await call("deleteAppMessage", { id }, { mutation: false });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  return (
    <div className="mx-auto max-w-5xl space-y-3">
      <h1 className="text-lg font-bold">App Messages</h1>
      <p className="text-xs text-steel">
        Service notices only — one slim banner, dismissible. Clevis never shows popups.
      </p>
      <Card>
        <CardTitle>New banner</CardTitle>
        <form onSubmit={create} className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <Label>Title</Label>
            <Input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div className="col-span-2">
            <Label>Body (optional)</Label>
            <Input value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
          </div>
          <div>
            <Label>Severity</Label>
            <Select
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value as AppMessage["severity"] })}
            >
              <option value="info">Info</option>
              <option value="success">Success</option>
              <option value="warning">Warning</option>
              <option value="error">Error</option>
            </Select>
          </div>
          <div>
            <Label>Audience</Label>
            <Select
              value={form.audience}
              onChange={(e) => setForm({ ...form, audience: e.target.value as AppMessage["audience"] })}
            >
              <option value="all">Everyone in the app</option>
              <option value="login">Sign-in screen</option>
            </Select>
          </div>
          {error ? <p className="col-span-2 text-xs text-danger">{error}</p> : null}
          <div className="col-span-2">
            <Button type="submit" busy={busy}>
              Publish banner
            </Button>
          </div>
        </form>
      </Card>

      <Card className="p-0">
        <div className="flex flex-wrap items-center gap-2 border-b border-steel/10 p-3">
          <SearchBox className="min-w-56 flex-1" value={view.query} onChange={view.setQuery} placeholder="Search banners…" />
          <Select className="w-auto py-1.5 text-sm" aria-label="Status" value={status} onChange={(e) => setStatus(e.target.value)}>
            <option value="all">All ({messages.length})</option>
            <option value="live">Live ({messages.filter((m) => m.active).length})</option>
            <option value="off">Off ({messages.filter((m) => !m.active).length})</option>
          </Select>
        </div>
        <div className="hidden items-center gap-4 border-b border-steel/10 px-4 py-2 sm:flex">
          <SortHeader label="Newest" k="updated" view={view} />
          <SortHeader label="Title" k="title" view={view} />
          <SortHeader label="Severity" k="severity" view={view} />
        </div>
        {view.page.map((m) => (
          <div key={m.id} className="flex items-center justify-between gap-3 border-b border-steel/10 px-4 py-2 last:border-0">
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <span className={cn("inline-block h-2 w-2 shrink-0 rounded-full", m.active ? "bg-success" : "bg-steel/40")} title={m.active ? "Live" : "Off"} />
                <span className="truncate text-sm font-medium text-ink">{m.title}</span>
                <span className={cn("shrink-0 rounded-full px-2 py-0.5 text-[11px] capitalize", SEVERITY_STYLE[m.severity])}>{m.severity}</span>
              </div>
              <div className="truncate pl-4 text-xs text-steel">
                {m.audience === "login" ? "Sign-in screen" : "Everyone in the app"}
                {m.body ? ` · ${m.body}` : ""}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <Button variant={m.active ? "secondary" : "ghost"} className="px-3 py-1 text-xs" onClick={() => void toggleActive(m)}>
                {m.active ? "Turn off" : "Turn on"}
              </Button>
              <ConfirmButton className="px-2 py-1 text-xs text-danger/80" confirm="Delete this banner?" confirmLabel="Delete" onConfirm={() => void remove(m.id)}>
                Delete
              </ConfirmButton>
            </div>
          </div>
        ))}
        {view.filtered.length === 0 ? <div className="px-4 py-8 text-center text-sm text-steel">{messages.length ? "Nothing matches." : "No banners."}</div> : null}
        <ListFooter shown={view.page.length} matching={view.filtered.length} total={view.total} onMore={view.more} onAll={view.all} noun="banners" />
      </Card>
    </div>
  );
}
